import { createApi } from '@reduxjs/toolkit/dist/query/react';
import { BASE_URL } from '../../api/baseURL';
import { axiosBaseQuery } from '../axiosBaseQuery';
import { PaginatedResponse, Response } from '../genericInterface';
import {
  IFee,
  IQuery,
  IShoppingEdit,
  IStore,
  IStoreCategory,
  IStoreProduct,
  IStoreStatus
} from './interface';

export const storeApi = createApi({
  reducerPath: 'storeApi',
  baseQuery: axiosBaseQuery({
    baseUrl: BASE_URL
  }),
  tagTypes: ['stores', 'products', 'fees'],
  endpoints: (builder) => ({
    getAllStores: builder.query<PaginatedResponse<IStore>, string | void>({
      query: (queryParams) => ({
        url: `/shopping/get-all-stores?${queryParams ?? ''}`,
        method: 'GET'
      }),
      providesTags: ['stores']
    }),
    addStore: builder.mutation<Response<IStore>, FormData>({
      query: (payload) => ({
        url: '/shopping/add-store',
        method: 'POST',
        body: payload
      }),
      invalidatesTags: [`stores`]
    }),
    getStoreById: builder.query<Response<IStore>, string>({
      query: (id) => ({
        url: `/shopping/get-store?id=${id}`,
        method: 'GET'
      }),
      providesTags: (_res, _err, arg) => [{ type: 'stores', id: arg }]
    }),
    editStore: builder.mutation<Response<IStore>, IQuery>({
      query: (payload) => ({
        url: `/shopping/edit-store?id=${payload.id}`,
        method: 'PATCH',
        body: payload.body
      }),
      invalidatesTags: ['stores']
    }),
    storeStatus: builder.mutation<Response<IStore>, IStoreStatus>({
      query: (payload) => ({
        url: `/shopping/edit-store-status?id=${payload.id}`,
        method: 'PATCH',
        body: payload.body
      }),
      invalidatesTags: ['stores']
    }),
    deleteStore: builder.mutation<Response<null>, string>({
      query: (payload) => ({
        url: `/shopping/delete-store?id=${payload}`,
        method: 'DELETE'
      }),
      invalidatesTags: ['stores']
    }),
    addProducts: builder.mutation<Response<IStoreProduct>, FormData>({
      query: (payload) => ({
        url: '/shopping/add-product',
        method: 'POST',
        body: payload
      }),
      invalidatesTags: ['products']
    }),
    getProductsByStoreId: builder.query<
      PaginatedResponse<IStoreProduct>,
      string
    >({
      query: (query) => ({
        url: `/shopping/get-products?${query}`,
        method: 'GET'
      }),
      providesTags: (_res, _err, arg) => [{ type: 'products', id: arg }]
    }),
    deleteStoreProduct: builder.mutation<Response<null>, string>({
      query: (payload) => ({
        url: `/shopping/delete-product?id=${payload}`,
        method: 'DELETE'
      }),
      invalidatesTags: ['stores', 'products']
    }),
    deleteStoreCategory: builder.mutation<Response<null>, string>({
      query: (payload) => ({
        url: `/shopping/delete-category?id=${payload}`,
        method: 'DELETE'
      }),
      invalidatesTags: ['stores']
    }),
    getCategories: builder.query<Response<IStoreCategory[]>, void>({
      query: () => ({
        url: `/shopping/get-categories`,
        method: 'GET'
      }),
      providesTags: ['stores']
    }),
    addCategory: builder.mutation<Response<IStoreCategory>, FormData>({
      query: (payload) => ({
        url: '/shopping/add-category',
        method: 'POST',
        body: payload
      }),
      invalidatesTags: ['stores']
    }),
    editStoreCategories: builder.mutation<
      Response<IStoreCategory>,
      IShoppingEdit
    >({
      query: (payload) => ({
        url: `/shopping/edit-category?id=${payload.id}`,
        method: 'PATCH',
        body: payload.body
      })
    }),
    setDeliveryFee: builder.mutation<
      Response<IFee>,
      {
        state: string;
        fee: number;
      }
    >({
      query: (body) => ({
        url: `/shopping/delivery-fee`,
        method: 'POST',
        body
      }),
      invalidatesTags: ['fees']
    }),
    getAllDeliveryFees: builder.query<Response<IFee[]>, void>({
      query: () => ({
        url: `/shopping/delivery-fee`,
        method: 'GET'
      }),
      providesTags: ['fees']
    }),
    editProducts: builder.mutation<
      Response<IStoreProduct>,
      { payload: FormData; id: string }
    >({
      query: (data) => ({
        url: `/shopping/edit-product?id=${data.id}`,
        method: 'PATCH',
        body: data.payload
      }),
      invalidatesTags: ['products']
    }),
    addBulkProducts: builder.mutation<Response<IStoreProduct>, FormData>({
      query: (payload) => ({
        url: '/shopping/add-bulk-products',
        method: 'POST',
        body: payload
      }),
      invalidatesTags: ['products']
    })
  })
});

export const {
  useGetAllStoresQuery,
  useAddStoreMutation,
  useEditStoreMutation,
  useDeleteStoreMutation,
  useGetStoreByIdQuery,
  useAddProductsMutation,
  useStoreStatusMutation,
  useAddCategoryMutation,
  useGetCategoriesQuery,
  useDeleteStoreCategoryMutation,
  useDeleteStoreProductMutation,
  useEditStoreCategoriesMutation,
  useSetDeliveryFeeMutation,
  useGetAllDeliveryFeesQuery,
  useGetProductsByStoreIdQuery,
  useEditProductsMutation,
  useAddBulkProductsMutation
} = storeApi;
